import { init, setDefaultEventParameters, trackEvent, trackException, trackPageView } from "../main/index.mjs";
import { getVueRoutePath, getVueRouteQuery } from "../utils/vue.mjs";
import { Env } from '@voghion/fe-util';
const SMALL_ELEMENT_VIEW_PERCENT = 0.5; // 小元素曝光区域比例，大于一定的比例才算曝光
const BIG_ELEMENT_VIEW_PERCENT = 0.3; // 大元素曝光区域比例，大于一定的比例才算曝光
const BIG_ELEMENT_VIEW_MIN_PIXEL = 242500; // 大元素最少像素
const EVENT_ELEMENT_VIEW = 'element_view'; // 元素曝光事件
const EVENT_ELEMENT_CLICK = 'element_click'; // 元素点击事件
const ELEMENT_NAME = 'element_name'; // 元素名称
const ELEMENT_NAME_ATTR = 'name'; // 元素名称
const WEB_ELEMENT_VIEW_PARAMS_KEY = 'data-view_params'; // 元素曝光参数 data-option
const WEB_ELEMENT_CLICK_PARAMS_KEY = 'data-click_params'; // 元素点击参数 data-option
/**
 * 触发元素曝光
 * @param el
 */
function trackElementView(el) {
    const params = JSON.parse(el.getAttribute(WEB_ELEMENT_VIEW_PARAMS_KEY) || '{}');
    trackEvent(EVENT_ELEMENT_VIEW, Object.assign(Object.assign({}, params), { [ELEMENT_NAME]: el.getAttribute(ELEMENT_NAME_ATTR) || '' }));
    el.viewed = true;
}
/**
 * vue 插件
 */
export default {
    install(app, options) {
        const setPageDefaultParameters = (router, to, from) => {
            var _a;
            const routePath = getVueRoutePath(router);
            const routeQuery = getVueRouteQuery(router);
            const pageName = (((_a = to.meta) === null || _a === void 0 ? void 0 : _a.pageName) || '');
            const params = {
                route_path: routePath,
                route_query: routeQuery,
                page_name: pageName,
                page_data_id: ''
            };
            setDefaultEventParameters(options.processPageDefaultParameters
                ? options.processPageDefaultParameters(params, to, from)
                : params);
        };
        const trackInstance = init(Object.assign(Object.assign({}, options), { 
            /**
             * 如果传入 router，强制设为 false，由 vue-router 来控制
             */
            autoTrackPageView: options.router ? false : options.autoTrackPageView }));
        app.config.globalProperties.$trackInstance = trackInstance;
        app.config.globalProperties.$trackEvent = trackEvent;
        app.config.globalProperties.$trackPageView = trackPageView;
        app.config.globalProperties.$trackException = trackException;
        if (options.router && Env.getInstance().isClient) {
            const autoTrackPageView = 'autoTrackPageView' in options ? options.autoTrackPageView : true;
            const { router } = options;
            router.afterEach(function (to, from) {
                setPageDefaultParameters(router, to, from);
                const pageViewManual = !!to.meta.pageViewManual;
                if (!pageViewManual && autoTrackPageView) {
                    setTimeout(() => {
                        trackPageView();
                    });
                }
            });
        }
        /**
         * 埋点相关的指令
         */
        let bigViewIntersectionObserver = null;
        let smallViewIntersectionObserver = null;
        function getIntersectionObserver(el) {
            const rect = el.getBoundingClientRect();
            const pixel = rect.width * rect.height * window.devicePixelRatio;
            let intersectionObserver = null;
            if (pixel >= BIG_ELEMENT_VIEW_MIN_PIXEL) {
                if (!bigViewIntersectionObserver) {
                    bigViewIntersectionObserver = new IntersectionObserver((entries) => {
                        for (const entry of entries) {
                            const target = entry.target;
                            if (!target.viewed && entry.intersectionRatio) {
                                trackElementView(target);
                            }
                        }
                    }, {
                        threshold: BIG_ELEMENT_VIEW_PERCENT
                    });
                }
                intersectionObserver = bigViewIntersectionObserver;
            }
            else {
                if (!smallViewIntersectionObserver) {
                    smallViewIntersectionObserver = new IntersectionObserver((entries) => {
                        for (const entry of entries) {
                            const target = entry.target;
                            if (!target.viewed && entry.intersectionRatio) {
                                trackElementView(target);
                            }
                        }
                    }, {
                        threshold: SMALL_ELEMENT_VIEW_PERCENT
                    });
                }
                intersectionObserver = smallViewIntersectionObserver;
            }
            return intersectionObserver;
        }
        /**
         * 曝光埋点指令
         */
        app.directive('track-view', {
            mounted(el, binding) {
                const intersectionObserver = getIntersectionObserver(el);
                intersectionObserver.observe(el);
                const params = binding.value || {};
                if (params) {
                    el.setAttribute(WEB_ELEMENT_VIEW_PARAMS_KEY, JSON.stringify(params));
                }
            },
            updated(el, binding) {
                const params = binding.value || {};
                if (params) {
                    el.setAttribute(WEB_ELEMENT_VIEW_PARAMS_KEY, JSON.stringify(params));
                }
            },
            beforeUnmount(el) {
                const intersectionObserver = getIntersectionObserver(el);
                intersectionObserver.unobserve(el);
                el.setAttribute(WEB_ELEMENT_VIEW_PARAMS_KEY, '');
            }
        });
        /**
         * 点击埋点指令
         */
        app.directive('track-click', {
            mounted(el, binding) {
                el.logClickHandler = () => {
                    const params = JSON.parse(el.getAttribute(WEB_ELEMENT_CLICK_PARAMS_KEY) || '{}');
                    trackEvent(EVENT_ELEMENT_CLICK, Object.assign(Object.assign({}, params), { [ELEMENT_NAME]: el.getAttribute(ELEMENT_NAME_ATTR) || '' }));
                };
                el.addEventListener('click', el.logClickHandler);
                const params = binding.value || {};
                if (params) {
                    el.setAttribute(WEB_ELEMENT_CLICK_PARAMS_KEY, JSON.stringify(params));
                }
            },
            updated(el, binding) {
                const params = binding.value || {};
                if (params) {
                    el.setAttribute(WEB_ELEMENT_CLICK_PARAMS_KEY, JSON.stringify(params));
                }
            },
            beforeUnmount(el) {
                if (el.logClickHandler) {
                    el.removeEventListener('click', el.logClickHandler);
                }
                el.setAttribute(WEB_ELEMENT_CLICK_PARAMS_KEY, '');
            }
        });
    }
};
